import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * Auth shape mirrors the JWT Keycloak returns.
 * In mock mode we synthesize one client-side; in oidc mode we fetch the
 * session from the BFF (which terminates OIDC and forwards to kcp).
 *
 * The lockout (5 attempts) and idle-timeout (1 hour) behaviors are
 * ported from the Flutter app — preserve them here so the UX matches.
 */
export type Session = {
  userId: string
  email: string
  name?: string
  orgId: string
  tenantId: string
  roles: string[]
  expiresAt?: string
  /** raw JWT — what gets attached as Bearer to BFF calls */
  token: string
  /**
   * Residency region the user's data lives in. GCP region names per §14
   * (matches proto Region enum + kcp shard names).
   * Sourced from the Keycloak JWT's region claim. Drives:
   *   - the residency-hostname redirect on first authed render
   *   - the workspace switcher's default selection
   *   - which Data API endpoint the SPA hits
   */
  region?: 'northamerica-northeast2' | 'us-east1' | 'us-east4' | 'europe-west1'
  /**
   * Fully-qualified host name the user should be on after login (e.g.
   * eu.app.integratrace.com). Empty string means "no residency redirect
   * required" — used by `localhost` / preview deploys.
   */
  residencyHost?: string
}

type AuthState = {
  session: Session | null
  loading: boolean
  /** Number of failed login attempts in the current lockout window. */
  failedAttempts: number
  /** When > now, login is locked out. */
  lockedUntil: number | null
  /** Why the last session ended — drives the login `?expired` banner. */
  endReason: 'idle' | 'manual' | null
}

/** Self-service sign-up payload — mirrors the Flutter create-account form. */
export type SignUpInput = {
  name: string
  email: string
  password: string
  jobTitle: string
}

type AuthContextValue = AuthState & {
  signIn: (email: string, password: string) => Promise<void>
  /**
   * Self-service account creation. Mock mode synthesizes a session (auto
   * sign-in); oidc mode hands off to Keycloak's hosted registration page.
   */
  signUp: (input: SignUpInput) => Promise<void>
  /**
   * Mock SSO sign-in — synthesizes a session through the same path as
   * `signIn`. In oidc mode Keycloak's hosted login renders brokered IdPs.
   */
  signInWithSso: (email: string) => Promise<void>
  /**
   * Kick off a password-reset email. Mock mode simulates the round-trip;
   * oidc mode sends the user to Keycloak's reset-credentials flow.
   */
  requestPasswordReset: (email: string) => Promise<void>
  /**
   * Complete a password reset using the emailed token. Mock mode validates
   * locally; oidc mode defers to Keycloak's own pages.
   */
  resetPassword: (token: string, password: string) => Promise<void>
  /**
   * Change the signed-in user's password (current → new). Mock mode
   * simulates; oidc mode drives Keycloak's UPDATE_PASSWORD required action.
   */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  signOut: (reason?: 'idle' | 'manual') => void
  /** Reset idle timer — call on any user activity. */
  poke: () => void
  /**
   * Epoch ms when the idle timer fires. Ref-backed (poke runs on mousemove),
   * so consumers poll it on their own interval instead of re-rendering.
   */
  getIdleDeadline: () => number | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

const MAX_FAILED_ATTEMPTS = 5
const LOCKOUT_MS = 30 * 60 * 1000        // 30 min after lockout
const IDLE_TIMEOUT_MS = 60 * 60 * 1000   // 1 hour idle → sign out

/**
 * Mock-auth flows POST to MSW-only /api/v1/auth/* endpoints purely so the
 * mock API's validation errors surface in the UI. When MSW is off (real-wire
 * dev: VITE_USE_MOCKS=false + VITE_AUTH_MODE=mock), those endpoints don't
 * exist on the real BFF — skip the round-trip and complete locally.
 */
const MOCKS_ON = import.meta.env.VITE_USE_MOCKS !== 'false'

/** A synthetic JWT used in mock mode. Shape matches what Keycloak will emit. */
function mintMockToken(claims: Omit<Session, 'token'>): string {
  // Not a real JWT — just a deterministic stand-in. The BFF will reject this
  // in oidc mode; that's the whole point of the swap surface.
  const header = btoa(JSON.stringify({ alg: 'mock', typ: 'JWT' }))
  const body = btoa(JSON.stringify(claims))
  return `${header}.${body}.mock-signature`
}

function makeMockSession(email: string, name?: string): Session {
  const claims: Omit<Session, 'token'> = {
    // The identity plane is synthetic in mock mode — the email the user typed
    // is the only real identity fact, so it IS the user id. org/tenant are
    // unknown until the org chooser picks a real workspace (`it.orgId`);
    // never invent fixture-style ids here, they leak into the session card.
    userId: email,
    email,
    name: name?.trim() || email.split('@')[0],
    orgId: '',
    tenantId: '',
    roles: ['admin'],
    expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
    // Mock-mode lands wherever the SPA is served — leaving region unset
    // makes residencyHostFor short-circuit (line ~172) so designer previews
    // don't get bounced to the prod regional hosts.
    region: undefined,
    residencyHost: '',
  }
  return { ...claims, token: mintMockToken(claims) }
}

/** Persist a freshly-minted session and reset auth state (idle timer started). */
function bootSession(
  session: Session,
  setState: (next: AuthState) => void,
  poke: () => void,
): void {
  sessionStorage.setItem('it.session', JSON.stringify(session))
  setState({
    session,
    loading: false,
    failedAttempts: 0,
    lockedUntil: null,
    endReason: null,
  })
  poke()
}

/** The base URL the oidc-mode redirects + session probe target. */
export function bffUrl(): string {
  return import.meta.env.VITE_BFF_URL ?? import.meta.env.VITE_API_URL ?? ''
}

/** Shape of the BFF `GET /api/v1/auth/session` response (routes/auth.ts). */
type BffSession = {
  userId: string
  email: string
  name?: string
  orgId?: string
  tenantId?: string
  roles?: string[]
  expiresAt?: string
  /** Access token — the SPA forwards this as Bearer to the Data API. */
  token?: string
}

/** Project the cookie-backed BFF session onto the SPA `Session` shape. */
function sessionFromBff(b: BffSession): Session {
  return {
    userId: b.userId,
    email: b.email,
    name: b.name?.trim() || undefined,
    orgId: b.orgId ?? '',
    tenantId: b.tenantId ?? '',
    roles: b.roles ?? [],
    expiresAt: b.expiresAt,
    token: b.token ?? '',
    // No region claim in the realm yet → no residency redirect (the live
    // global stamp is same-origin). Wired when the claim lands.
    region: undefined,
    residencyHost: '',
  }
}

const regionToHost: Record<NonNullable<Session['region']>, string> = {
  // ponytail: env-hardcoded to the dev tier. The global stamp (residency `ca`)
  // serves app.dev.integratrace.com same-origin; the residency redirect is inert
  // in dev (no region claim yet). Make these env-driven if prod ships multi-region.
  'northamerica-northeast2': 'app.dev.integratrace.com',
  'us-east1': 'us.app.dev.integratrace.com',
  'us-east4': 'us.app.dev.integratrace.com',
  'europe-west1': 'eu.app.dev.integratrace.com',
}

/**
 * Determines the host the user should be on for their region. Returns
 * empty string when no redirect is needed (localhost, preview deploys,
 * already-on-target-host).
 */
export function residencyHostFor(session: Session, currentHost: string): string {
  if (!session.region) return ''
  // `currentHost` is `window.location.host`, which includes the port for
  // non-standard ports (Vite dev = `localhost:5173`). Compare on hostname
  // only so the dev-server short-circuit fires.
  const hostname = currentHost.split(':')[0]
  if (hostname === 'localhost' || hostname.endsWith('.localhost') || hostname === '127.0.0.1') return ''
  // Operators can override per-deployment via env: when set, prefer the
  // session's explicit residencyHost.
  const target = session.residencyHost?.trim() ? session.residencyHost : regionToHost[session.region]
  if (!target || target === hostname) return ''
  return target
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session: null,
    loading: true,
    failedAttempts: 0,
    lockedUntil: null,
    endReason: null,
  })

  const idleTimer = useRef<number | null>(null)
  const idleDeadline = useRef<number | null>(null)

  const signOut = useCallback((reason: 'idle' | 'manual' = 'manual') => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current)
    idleDeadline.current = null
    const mode = import.meta.env.VITE_AUTH_MODE ?? 'mock'
    if (mode !== 'mock') {
      // Drop the server-side cookie. Fire-and-forget — local state clears
      // regardless so the UI signs out even if the BFF is unreachable.
      void fetch(`${bffUrl()}/api/v1/auth/logout`, { method: 'POST', credentials: 'include' })
    }
    sessionStorage.removeItem('it.session')
    setState((s) => ({ ...s, session: null, endReason: reason }))
  }, [])

  const poke = useCallback(() => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current)
    idleDeadline.current = Date.now() + IDLE_TIMEOUT_MS
    idleTimer.current = window.setTimeout(() => {
      // eslint-disable-next-line no-console
      console.info('[auth] idle timeout — signing out')
      signOut('idle')
    }, IDLE_TIMEOUT_MS)
  }, [signOut])

  const getIdleDeadline = useCallback(() => idleDeadline.current, [])

  // Boot: restore the session.
  //   - mock mode: sessionStorage is the source of truth (synthesized locally).
  //   - real OIDC: the httpOnly cookie is the source of truth — probe the BFF
  //     and mirror the result into sessionStorage so the router guard and
  //     api/client keep reading the same place. `loading` stays true until the
  //     probe resolves so AuthGate doesn't bounce to /login mid-flight.
  useEffect(() => {
    const mode = import.meta.env.VITE_AUTH_MODE ?? 'mock'

    if (mode === 'mock') {
      const raw = sessionStorage.getItem('it.session')
      if (raw) {
        try {
          const session = JSON.parse(raw) as Session
          setState((s) => ({ ...s, session, loading: false }))
          poke()
          return
        } catch {
          sessionStorage.removeItem('it.session')
        }
      }
      setState((s) => ({ ...s, loading: false }))
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const res = await fetch(`${bffUrl()}/api/v1/auth/session`, { credentials: 'include' })
        if (cancelled) return
        if (!res.ok) {
          sessionStorage.removeItem('it.session')
          setState((s) => ({ ...s, session: null, loading: false }))
          return
        }
        const session = sessionFromBff((await res.json()) as BffSession)
        if (cancelled) return
        bootSession(session, setState, poke)
      } catch {
        if (cancelled) return
        sessionStorage.removeItem('it.session')
        setState((s) => ({ ...s, session: null, loading: false }))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [poke])

  // Wire idle-reset listeners while signed in.
  useEffect(() => {
    if (!state.session) return
    const events = ['click', 'keydown', 'mousemove', 'scroll'] as const
    const handler = () => poke()
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }))
    return () => events.forEach((e) => window.removeEventListener(e, handler))
  }, [state.session, poke])

  // Residency-host redirect (plan §5 phase 5): on first authed render,
  // check whether the current host matches the session's residency
  // region. If not, redirect — Cloudflare's DNS plus per-region kcp/data
  // routing only work when the SPA is on the right hostname.
  useEffect(() => {
    if (!state.session) return
    const target = residencyHostFor(state.session, window.location.host)
    if (!target) return
    // Preserve path + query so deep links survive the redirect.
    const url = new URL(window.location.href)
    url.host = target
    // Replace, not assign — back-button shouldn't loop the user through
    // the wrong-residency host.
    window.location.replace(url.toString())
  }, [state.session])

  const signIn = useCallback(
    async (email: string, _password: string) => {
      if (state.lockedUntil && state.lockedUntil > Date.now()) {
        throw new Error('Account locked. Try again later.')
      }

      const mode = import.meta.env.VITE_AUTH_MODE ?? 'mock'

      if (mode === 'mock') {
        // Mock-mode: any non-empty email succeeds, "wrong@*" fails to demo lockout.
        if (email === 'wrong@example.com') {
          setState((s) => {
            const next = s.failedAttempts + 1
            const lockedUntil = next >= MAX_FAILED_ATTEMPTS ? Date.now() + LOCKOUT_MS : null
            return { ...s, failedAttempts: next, lockedUntil }
          })
          throw new Error('Invalid credentials')
        }
        bootSession(makeMockSession(email), setState, poke)
        return
      }

      // Real OIDC mode — the BFF redirects to Keycloak; this client only kicks
      // it off, then hydrates the cookie-backed session on the return boot.
      window.location.href = `${bffUrl()}/api/v1/auth/login`
    },
    [state.lockedUntil, poke],
  )

  const signUp = useCallback(
    async (input: SignUpInput) => {
      const mode = import.meta.env.VITE_AUTH_MODE ?? 'mock'

      if (mode === 'mock') {
        if (MOCKS_ON) {
          // POST to the mock BFF so validation errors surface like the real API.
          const res = await fetch('/api/v1/auth/signup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: input.name.trim(),
              email: input.email.trim(),
              password: input.password,
              jobTitle: input.jobTitle.trim(),
            }),
          })
          if (!res.ok) {
            const body = (await res.json().catch(() => null)) as { message?: string } | null
            throw new Error(body?.message ?? 'Could not create account')
          }
        }
        // Mock email-confirmation is disabled → auto sign-in (matches the
        // Flutter "session != null" branch).
        bootSession(makeMockSession(input.email.trim(), input.name), setState, poke)
        return
      }

      // Real OIDC mode — hand off to Keycloak's hosted registration page. Org
      // membership stays invite-gated (create-or-join at /orgs after login).
      window.location.href = `${bffUrl()}/api/v1/auth/register`
    },
    [poke],
  )

  const signInWithSso = useCallback(
    async (email: string) => {
      const mode = import.meta.env.VITE_AUTH_MODE ?? 'mock'

      if (mode === 'mock') {
        // SSO is a separate trust path: it ignores the form lockout and
        // synthesizes a session just like signIn.
        bootSession(makeMockSession(email), setState, poke)
        return
      }

      // Real OIDC mode — Keycloak's hosted login page already renders every
      // brokered identity provider, so SSO is just the normal login redirect.
      window.location.href = `${bffUrl()}/api/v1/auth/login`
    },
    [poke],
  )

  const requestPasswordReset = useCallback(async (email: string) => {
    const mode = import.meta.env.VITE_AUTH_MODE ?? 'mock'

    if (mode === 'mock') {
      if (MOCKS_ON) {
        const res = await fetch('/api/v1/auth/request-password-reset', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim() }),
        })
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { message?: string } | null
          throw new Error(body?.message ?? 'Could not send the reset link')
        }
      }
      return
    }

    // Real OIDC mode — Keycloak owns credential recovery. Its hosted login
    // page carries the "Forgot password?" link (realm reset-credentials flow),
    // so send the user there.
    window.location.href = `${bffUrl()}/api/v1/auth/login`
  }, [])

  const resetPassword = useCallback(async (token: string, password: string) => {
    const mode = import.meta.env.VITE_AUTH_MODE ?? 'mock'

    if (mode === 'mock') {
      if (MOCKS_ON) {
        const res = await fetch('/api/v1/auth/reset-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, password }),
        })
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { message?: string } | null
          throw new Error(body?.message ?? 'Could not reset your password')
        }
      }
      return
    }

    // Real OIDC mode — the reset is completed on Keycloak's own pages; the SPA
    // reset form is never reached. Bounce to login if we somehow land here.
    window.location.href = `${bffUrl()}/api/v1/auth/login`
  }, [])

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const mode = import.meta.env.VITE_AUTH_MODE ?? 'mock'

      if (mode === 'mock') {
        if (MOCKS_ON) {
          const res = await fetch('/api/v1/auth/change-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ currentPassword, newPassword }),
          })
          if (!res.ok) {
            const body = (await res.json().catch(() => null)) as { message?: string } | null
            throw new Error(body?.message ?? 'Could not change your password')
          }
        }
        return
      }

      // Real OIDC mode — trigger Keycloak's UPDATE_PASSWORD required action via
      // the BFF (kc_action on a fresh auth request; returns to the app after).
      window.location.href = `${bffUrl()}/api/v1/auth/account?action=UPDATE_PASSWORD`
    },
    [],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signIn,
      signUp,
      signInWithSso,
      requestPasswordReset,
      resetPassword,
      changePassword,
      signOut,
      poke,
      getIdleDeadline,
    }),
    [
      state,
      signIn,
      signUp,
      signInWithSso,
      requestPasswordReset,
      resetPassword,
      changePassword,
      signOut,
      poke,
      getIdleDeadline,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

// Vite cannot Fast Refresh this module (mixed component + plain exports), so
// an edit while a tab is open hot-swaps it and can leave the live tree with a
// split auth context — a frozen page a fresh load can't reproduce. Accept the
// update and do a clean full page reload instead of a risky hot patch.
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    window.location.reload()
  })
}
